import _ from 'lodash';
import * as ActionTypes from '../actions/actionTypes';
import incident_types from '../config/incident_types';
import {PRESCRIPTIONS_CONFIG} from '../config/prescriptions';
import {INCIDENT_PRESCRIPTION_STATUS, INCIDENT_TYPE_KEYS} from '../Constants';
import {convertValue} from '../utils/point_utils';
import {calcPrescriptionsHaloValue} from '../utils/prescriptions_utils';
import {formatForGraph} from '../utils/time_utils';

const _createInitialState = () => {
  let init = {
    layers: _.sortBy(incident_types, ['displayName']),
    polygons: null,
    polygons_map: null,
    prescriptions_map: null,
    active: null,
    activateWhenLoaded: null,
    data: {},
    prescriptions_updated_at: new Date().getMilliseconds()
  };

  incident_types.forEach((type) => {
    init.data[type.id.toLowerCase()] = null;
  });

  return init;
};

const _startingPrescriptionStatus = (prescriptionId) => {
  if (!prescriptionId) {
    return INCIDENT_PRESCRIPTION_STATUS.NO_PRESCRIPTION;
  }
  if (!PRESCRIPTIONS_CONFIG[prescriptionId]) {
    return INCIDENT_PRESCRIPTION_STATUS.UNKNOWN_PRESCRIPTION;
  }
  return INCIDENT_PRESCRIPTION_STATUS.PRESCRIPTION_NOT_LOADED;
};

const _findFeature = (state, eventId) => {
  const selectableLayers = state.layers.filter((l) => l.active && l.selectable);
  let foundFeature = null;
  selectableLayers.forEach((l) => {
    const data = state.data[l.id.toLowerCase()];
    if (data) {
      const found = _.find(data.features, (f) => f.eventId === eventId);
      if (found) {
        foundFeature = found;
      }
    }
  });
  return foundFeature;
};

const _populatePrescriptionData = (burn, prescription) => {
  burn.prescriptionId = prescription ? prescription.prescriptionId : null;
  burn.properties.Prescription = prescription ? prescription.prescription : '';
  burn.prescriptionStatus = _startingPrescriptionStatus(burn.prescriptionId);
  burn.prescriptionWeather = prescription ? prescription.weather : null;
};

const reducer = (state = _createInitialState(), action) => {
  switch (action.type) {

    case ActionTypes.TOGGLE_INCIDENT_TYPE: {
      const newLayers = state.layers.map((value) => {
        const new_value = { ...value };
        if (value.id === action.id) {
          new_value.active = !value.active;
        }
        return new_value;
      });
      return {
        ...state,
        layers: newLayers
      };
    }

    case ActionTypes.POPULATE_BUSHFIRE_DETAILS: {
      const layer = state.layers.find(layer => INCIDENT_TYPE_KEYS.BUSHFIRES === layer.id);
      layer.updated_at = action.updated_at;
      layer.poller = action.poller;
      action.bushfires.updated_at = action.updated_at;

      const newData = { ...state.data };
      newData.bushfires = action.bushfires;
      return { ...state, data: newData };
    }

    case ActionTypes.POPULATE_BURN_DETAILS: {
      const layer = state.layers.find(layer => action.incidentTypeKey === layer.id);
      const dataFieldName = layer.id.toLowerCase();
      layer.updated_at = action.updated_at;
      layer.poller = action.poller;
      action.burns.updated_at = action.updated_at;

      action.burns.features.forEach((burn) => {
        burn.dataFieldName = dataFieldName;
        if (state.prescriptions_map) {
          _populatePrescriptionData(burn, state.prescriptions_map[burn.properties.Number]);
        } else {
          burn.prescriptionStatus = INCIDENT_PRESCRIPTION_STATUS.PRESCRIPTION_NOT_LOADED;
        }
      });

      const newData = { ...state.data };
      newData[dataFieldName] = action.burns;
      return {
        ...state,
        data: newData
      };
    }

    case ActionTypes.CLEAR_BURN_PRESCRIPTION_WEATHER:
    case ActionTypes.CLEAR_AND_REFRESH_BURN_PRESCRIPTION_WEATHER: {
      const newData = { ...state.data };
      _.forOwn(newData, (burnList) => {
        if (burnList) {
          burnList.features.forEach(b => {
            if (b.prescriptionStatus === INCIDENT_PRESCRIPTION_STATUS.PRESCRIPTION_LOADED) {
              b.prescriptionStatus = INCIDENT_PRESCRIPTION_STATUS.PRESCRIPTION_NOT_LOADED;
            }
          });
        }
      });
      return { ...state, prescriptions_updated_at: new Date().getMilliseconds(), data: newData };
    }

    case ActionTypes.POPULATE_BURN_PRESCRIPTION_WEATHER: {
      const burn = action.burn;
      const burnList = state.data[burn.dataFieldName];

      const burnIdx = burnList ? _.findIndex(burnList.features, b => b.eventId === burn.eventId) : -1;
      const rawWd = action.data[formatForGraph(action.time)];

      if (!rawWd) {
        return state;
      }
      if (rawWd && burnIdx > -1) {
        // Apply ConvertValue to each wd variable.
        const wd = _.mapValues(rawWd, (value, prefix) => {
          const nonMaxPrefix = prefix.startsWith('MAX_') ? prefix.substring(4) : prefix;
          return convertValue(nonMaxPrefix, value);
        });

        const newState = { ...state };
        const newBurn = {
          ...burnList.features[burnIdx]
        };
        calcPrescriptionsHaloValue(newBurn, wd);
        newState.data[burn.dataFieldName].features[burnIdx] = newBurn;
        return newState;
      }

      return state;
    }

    case ActionTypes.REFRESH_BURN_PRESCRIPTIONS_COMPLETE:
      return { ...state, prescriptions_updated_at: new Date().getMilliseconds() };

    case ActionTypes.POPULATE_BURNT_AREA_POLYGONS: {
      const layer = state.layers.find(
        layer => INCIDENT_TYPE_KEYS.CURRENT_BURNT_AREA === layer.id
      );
      layer.updated_at = action.updated_at;
      action.burntArea.updated_at = action.updated_at;

      const newData = { ...state.data };
      newData.current_burnt_area = action.burntArea;
      return { ...state, data: newData };
    }

    case ActionTypes.POPULATE_BURN_POLYGONS: {
      action.polygons.updated_at = action.updated_at;
      if (action.polygons.features) {
        return {
          ...state,
          polygons: action.polygons,
          polygons_map: _.keyBy(action.polygons.features, p => p.eventId)
        };
      }
      return state;
    }

    case ActionTypes.POPULATE_BURN_PRESCRIPTIONS: {
      const prescriptions_map = _.keyBy(action.prescriptions, p => p.burnNumber);
      const new_data = { ...state.data };

      // New prescription data has arrived. Check for active burn layers and update.
      state.layers.forEach(l => {
        if (l.active && l.id.startsWith('BURNS_')) {
          var layerBurns = new_data[l.id.toLowerCase()];
          if (layerBurns) {
            layerBurns.features.forEach(burn => {
              _populatePrescriptionData(burn, prescriptions_map[burn.properties.Number]);
            });
          }
        }
      });

      return {
        ...state,
        data: new_data,
        prescriptions_map
      };
    }

    case ActionTypes.SET_ACTIVE_INCIDENT_FEATURE:
    case ActionTypes.SET_ACTIVE_INCIDENT: {
      const feature = _findFeature(state, action.eventId);

      if (!feature) {
        return state;
      }

      return {
        ...state,
        activateWhenLoaded: null,
        active: feature
      };
    }

    case ActionTypes.ACTIVATE_INCIDENT_WHEN_LOADED: {
      const feature = _findFeature(state, action.eventId);
      if (feature) {
        return {
          ...state,
          activateWhenLoaded: null,
          active: feature
        };
      } else {
        return {
          ...state,
          activateWhenLoaded: action.eventId
        };
      }
    }

    case ActionTypes.SET_ACTIVE_POINT: {
      return {
        ...state,
        active: null
      };
    }

    default:
      return state;
  }
};

export default reducer;



// WEBPACK FOOTER //
// ./src/reducers/incidents.js